import React, { useRef, useEffect, useMemo, useState } from 'react';
import SEO from './SEO';
import JsonLd from './JsonLd';
import { FIRMA, MAPS_LINK } from '../data/firma';
import { BUSINESS_SCHEMA, PERSON_SCHEMA } from '../data/schema';
import { OPINIE } from '../data/opinie';
import styles from '../css/modules/Homepage.module.css';

// Dane strukturalne firmy zyja w src/data/schema.js - tutaj tylko je osadzamy.
// Dzieki jednemu @id "#business" caly serwis opisuje JEDEN podmiot, zamiast
// powielac wezly z roznymi adresami i godzinami (spec sekcja 12).

// --- Ikony (inline SVG, dziedziczą currentColor) ---
const IconPlay = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M8 5.14v14l11-7-11-7z" fill="currentColor" stroke="none" /></svg>
);
const IconSearch = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
);
const IconHeart = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M20.8 6.6a5 5 0 0 0-7.1 0L12 8.3l-1.7-1.7a5 5 0 1 0-7.1 7.1L12 21l8.8-7.3a5 5 0 0 0 0-7.1z" /></svg>
);
const IconSpark = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" /></svg>
);
const IconStar = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7L12 2z" /></svg>
);
const IconPhone = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>
);
const IconPin = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
);
const IconMail = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
);
const IconClock = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
);
const IconArrow = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

const REASON_ICONS = [IconSpark, IconSearch, IconHeart];

const REASONS = [
  {
    title: 'Terapia SI w formie zabawy',
    text: process.env.REACT_APP_WHY_LI1
      ? process.env.REACT_APP_WHY_LI1.replace(/<[^>]+>/g, '').replace(/^[^:]+:\s*/, '')
      : 'Zajęcia integracji sensorycznej w Olsztynie wspierają rozwój motoryki, koncentracji i koordynacji u dzieci poprzez atrakcyjne ćwiczenia ruchowe.',
  },
  {
    title: 'Indywidualne podejście',
    text: process.env.REACT_APP_WHY_LI2
      ? process.env.REACT_APP_WHY_LI2.replace(/<[^>]+>/g, '').replace(/^[^:]+:\s*/, '')
      : 'Zajęcia SI dla dzieci w naszym gabinecie w Olsztynie są dopasowane do potrzeb dziecka, dzięki czemu terapia przebiega bez stresu i przynosi realne efekty.',
  },
  {
    title: 'Sprawdzone metody',
    text: process.env.REACT_APP_WHY_LI3
      ? process.env.REACT_APP_WHY_LI3.replace(/<[^>]+>/g, '').replace(/^[^:]+:\s*/, '')
      : 'Terapia SI w Olsztynie oparta na sprawdzonych metodach integracji sensorycznej oraz aktualnej wiedzy z zakresu wspierania rozwoju dziecka.',
  },
];

/* Etykiety linkow sa konkretne ("Jak wyglada diagnoza", "Zobacz cennik"),
   bez "Dowiedz sie wiecej" i "Sprawdz" (spec sekcja 1). */
const SERVICES = [
  { to: '/diagnoza-sensoryczna-olsztyn/', img: '/image/diagnoza-integracji-sensorycznej-olsztyn.webp', title: 'Diagnoza sensoryczna', text: 'Wywiad, badanie dziecka i pisemna opinia z zaleceniami.', cta: 'Jak wygląda diagnoza', featured: true },
  { to: '/terapia-integracji-sensorycznej-olsztyn/', img: '/image/terapia-sensoryczna-integracja-sensoryczna-sensorini-olsztyn.webp', title: 'Terapia integracji sensorycznej', text: 'Zajęcia indywidualne prowadzone według planu po diagnozie.', cta: 'Zobacz, jak pracujemy' },
  { to: '/terapia-reki/', img: '/image/terapia-reki.webp', title: 'Terapia ręki', text: 'Usprawnianie motoryki małej, chwytu i przygotowanie do pisania.', cta: 'Poznaj terapię ręki' },
  { to: '/trening-umiejetnosci-spolecznych/', img: '/image/trening-umiejetnosci-spolecznych-TUS.webp', title: 'Trening umiejętności społecznych (TUS)', text: 'Nauka relacji, emocji i współpracy w grupie rówieśniczej.', cta: 'Poznaj zajęcia TUS' },
  { to: '/cennik/', img: '/image/sensorini-Olsztyn.webp', title: 'Cennik', text: 'Ceny diagnozy, konsultacji i zajęć oraz zasady odwoływania wizyt.', cta: 'Zobacz cennik' },
  { to: '/kiedy-do-terapeuty/', img: '/image/kiedy-do-terapeuty.webp', title: 'Kiedy do terapeuty?', text: 'Sygnały, które warto skonsultować z terapeutą SI.', cta: 'Sprawdź objawy' },
];


/*
 * Skrot do poradnika na blogu (/integracja-sensoryczna/).
 *
 * Blog ma ponad 500 tekstow, ale ze stron ofertowych prowadzil do niego
 * wylacznie link schowany w menu pod hamburgerem - zaden widoczny link
 * kontekstowy. Te cztery pozycje to teksty o najwyzszej intencji zakupowej;
 * opisowa etykieta linku mowi wyszukiwarce, czego dotyczy cel.
 */
const PORADNIK = [
  {
    to: '/integracja-sensoryczna/ile-kosztuje-diagnoza-integracji-sensorycznej-olsztyn',
    title: 'Ile kosztuje diagnoza integracji sensorycznej',
    text: 'Co składa się na koszt i co powinno być w cenie rzetelnego badania.',
  },
  {
    to: '/integracja-sensoryczna/do-ktorej-poradni-psychologiczno-pedagogicznej-olsztyn-rejonizacja',
    title: 'Do której poradni w Olsztynie',
    text: 'Rejonizacja, adresy i to, czego rodzice zwykle nie wiedzą o orzeczeniach.',
  },
  {
    to: '/integracja-sensoryczna/opinia-po-diagnozie-si-dla-przedszkola-i-szkoly',
    title: 'Opinia po diagnozie dla przedszkola i szkoły',
    text: 'Co zawiera dokument i jak realnie z niego skorzystać w placówce.',
  },
  {
    to: '/integracja-sensoryczna/jak-przygotowac-dziecko-do-diagnozy-si-olsztyn',
    title: 'Jak przygotować dziecko do diagnozy',
    text: 'Co zabrać, jak wytłumaczyć dziecku wizytę i czego nie robić przed badaniem.',
  },
];

const ReviewCard = React.memo(function ReviewCard({ name, tag, review }) {
  return (
    <figure className={styles.reviewCard}>
      <div className={styles.stars} role="img" aria-label="Ocena 5 na 5">
        {[0, 1, 2, 3, 4].map((i) => <IconStar key={i} className={styles.starIcon} aria-hidden="true" />)}
      </div>
      <blockquote className={styles.reviewText}>{review}</blockquote>
      <figcaption className={styles.reviewMeta}>
        <span className={styles.reviewName}>{name}</span>
        <span className={styles.reviewTag}>{tag}</span>
      </figcaption>
    </figure>
  );
});

const Homepage = () => {
  const scrollContainerRef = useRef(null);
  const heroMediaRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [canAutoplay, setCanAutoplay] = useState(true);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setCanAutoplay(!media.matches);
    update();
    try {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    } catch {
      media.addListener && media.addListener(update);
      return () => media.removeListener && media.removeListener(update);
    }
  }, []);

  useEffect(() => {
    if (!heroMediaRef.current) return undefined;

    /* Tlo wideo (600 kB) jest ozdoba - plakat .webp wazy 21 kB i to on jest
       elementem LCP. Na telefonie, przy oszczedzaniu danych albo przy
       ograniczonej animacji nie ma powodu dociagac filmu: uzytkownik zobaczy
       ten sam kadr, a strona nie placi za to transferem. */
    const oszczedzaDane = navigator.connection && navigator.connection.saveData;
    const drobnyEkran = window.matchMedia('(max-width: 767px)').matches;
    if (oszczedzaDane || drobnyEkran || !canAutoplay) return undefined;

    /* Film wazy ok. 600 kB i na komputerze potrafi zjesc wieksza czesc pasma
       w pierwszej sekundzie, opozniajac plakat, ktory JEST elementem LCP.
       Dlatego pobieranie startuje dopiero po zdarzeniu load, w pierwszej
       wolnej chwili - do tego czasu widac plakat, wiec kadr jest ten sam. */
    let io = null;
    let anulowane = false;

    const obserwuj = () => {
      if (anulowane || !heroMediaRef.current) return;
      io = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && !videoSrc) {
            setVideoSrc('/backgrounds/sensorini-video_girl_ball.mp4');
            io.disconnect();
          }
        },
        { rootMargin: '200px 0px' }
      );
      io.observe(heroMediaRef.current);
    };

    const zaplanuj = () => {
      const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 400));
      idle(obserwuj, { timeout: 2000 });
    };
    if (document.readyState === 'complete') zaplanuj();
    else window.addEventListener('load', zaplanuj);

    return () => {
      anulowane = true;
      window.removeEventListener('load', zaplanuj);
      if (io) io.disconnect();
    };
  }, [videoSrc, canAutoplay]);

  const reviews = useMemo(() => OPINIE, []);

  const scrollLeft = () => scrollContainerRef.current?.scrollBy({ left: -340, behavior: 'smooth' });
  const scrollRight = () => scrollContainerRef.current?.scrollBy({ left: 340, behavior: 'smooth' });

  const mainHeader = process.env.REACT_APP_WHY_MAIN_HEADER || 'Diagnoza i terapia integracji sensorycznej w Olsztynie';

  return (
    <div className={styles.page}>
      <SEO
        title="Sensorini Olsztyn - diagnoza i terapia integracji sensorycznej"
        description="Diagnoza i terapia integracji sensorycznej dla dzieci w Olsztynie. Gabinet Sensorini, ul. Partyzantów 31/5. Umów diagnozę: 663 518 937."
        pathname="/"
        type="website"
        image="/sensorini-logo.png"
      />
      <JsonLd data={[BUSINESS_SCHEMA, PERSON_SCHEMA]} />

      {/* ================= HERO ================= */}
      <section className={styles.hero}>
        <span className={`${styles.blob} ${styles.blobA}`} aria-hidden="true" />
        <span className={`${styles.blob} ${styles.blobB}`} aria-hidden="true" />
        <span className={`${styles.blob} ${styles.blobC}`} aria-hidden="true" />

        <div className={styles.heroInner}>
          <div className={styles.heroText}>
            <span className={styles.eyebrow}>
              <IconPin className={styles.eyebrowIcon} /> Integracja Sensoryczna • Olsztyn
            </span>
            <h1 className={styles.mainHeader}>{mainHeader}</h1>
            <p className={styles.heroLead}>
              {process.env.REACT_APP_WHY_INTRO ||
                'Rozwój przez zabawę i ruch - wspieramy dzieci w codziennych wyzwaniach. Profesjonalna diagnoza i terapia SI dopasowana do Twojego dziecka.'}
            </p>

            <div className={styles.heroCtas}>
              <a href="/diagnoza-sensoryczna-olsztyn/" className={styles.btnPrimary} data-cta="booking" data-cta-location="hero">
                Umów diagnozę <IconArrow className={styles.btnArrow} />
              </a>
              <a href={`tel:${FIRMA.telefonE164}`} className={styles.btnGhost}>
                <IconPhone className={styles.btnIcon} /> {FIRMA.telefon}
              </a>
            </div>

            <ul className={styles.heroChips} aria-label="Nasze atuty">
              <li><span className={styles.dot} /> Doświadczony terapeuta SI</li>
              <li><span className={styles.dot} /> Indywidualne podejście</li>
              <li><span className={styles.dot} /> Diagnoza + terapia</li>
            </ul>
          </div>

          <div className={styles.heroMedia} ref={heroMediaRef}>
            <div className={styles.mediaFrame}>
              <video
                className={styles.heroVideo}
                muted
                loop
                playsInline
                preload="metadata"
                poster="/image/sensorini-olsztyn-terapia-dla-dziecka.webp"
                autoPlay={canAutoplay}
                src={videoSrc || undefined}
              >
                Terapia Integracji Sensorycznej Olsztyn - wideo.
              </video>
              {!canAutoplay && <span className={styles.playHint}><IconPlay /></span>}
            </div>
            <div className={styles.floatCard}>
              <span className={styles.floatIcon}><IconHeart /></span>
              <div>
                <strong>Dzieci uwielbiają</strong>
                <span>naukę przez zabawę</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= KWESTIONARIUSZ ================= */}
      <section className={styles.quizStrip}>
        <div className={styles.quizStripInner}>
          <p className={styles.quizStripText}>
            Kwestionariusz sensomotoryczny wypełnia rodzic - zajmuje około 15 minut.
            <span>Odpowiedzi trafiają do terapeuty i skracają pierwszą wizytę.</span>
          </p>
          <a
            href="/kwestionariusz-sensomotoryczny/"
            className={styles.btnDeep}
            data-cta="kwestionariusz"
            data-cta-location="home-strip"
          >
            Wypełnij kwestionariusz sensomotoryczny przed diagnozą Twojego dziecka
            <IconArrow className={styles.btnArrow} />
          </a>
        </div>
      </section>

      {/* ================= DLACZEGO WARTO ================= */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <span className={styles.kicker}>Dlaczego Sensorini</span>
          <h2 className={styles.sectionTitle}>{process.env.REACT_APP_WHY_SUBHEADER || 'Dlaczego warto nam zaufać?'}</h2>
        </div>
        <div className={styles.featureGrid}>
          {REASONS.map((r, i) => {
            const Icon = REASON_ICONS[i % REASON_ICONS.length];
            return (
              <article key={r.title} className={styles.featureCard}>
                <span className={styles.featureIcon}><Icon /></span>
                <h3>{r.title}</h3>
                <p>{r.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* ================= OFERTA ================= */}
      <section className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionHead}>
          <span className={styles.kicker}>Nasza oferta</span>
          <h2 className={styles.sectionTitle}>Jak wspieramy Twoje dziecko</h2>
          <p className={styles.sectionSub}>Diagnoza i terapia integracji sensorycznej w Olsztynie - wybierz obszar, który Cię interesuje.</p>
        </div>
        <div className={styles.serviceGrid}>
          {SERVICES.map((s) => (
            <a key={s.to} href={s.to} className={`${styles.serviceCard} ${s.featured ? styles.serviceFeatured : ''}`}>
              <div className={styles.serviceImgWrap}>
                <img src={s.img} alt={s.title + ' Olsztyn'} loading="lazy" className={styles.serviceImg} />
                {s.featured && <span className={styles.serviceBadge}>Najczęściej wybierane</span>}
              </div>
              <div className={styles.serviceBody}>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
                <span className={styles.serviceLink}>{s.cta} <IconArrow className={styles.linkArrow} /></span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ================= O MNIE ================= */}
      <section className={styles.section}>
        <div className={styles.about}>
          <div className={styles.aboutMedia}>
            <img src="/image/anna-sensorini-olsztyn-terapia-dla-dziecka-Anna.webp" alt="Anna - terapeuta integracji sensorycznej w Olsztynie" loading="lazy" />
          </div>
          <div className={styles.aboutText}>
            <span className={styles.kicker}>Poznaj terapeutę</span>
            <h2 className={styles.sectionTitle}>Ciepło, cierpliwość i doświadczenie</h2>
            <p>
              W Sensorini każde dziecko jest traktowane indywidualnie. Łączę wiedzę z zakresu integracji
              sensorycznej z empatią i zabawą, aby terapia przebiegała bez stresu, a rodzice widzieli
              realne postępy swojego dziecka.
            </p>
            <ul className={styles.aboutList}>
              <li><span className={styles.tick} /> Certyfikowany terapeuta SI</li>
              <li><span className={styles.tick} /> Bezpieczna, przyjazna atmosfera</li>
              <li><span className={styles.tick} /> Współpraca z rodzicami na każdym etapie</li>
            </ul>
            <a href="/o-mnie/" className={styles.btnOutline}>Poznaj kwalifikacje i doświadczenie <IconArrow className={styles.btnArrow} /></a>
          </div>
        </div>
      </section>

      {/* ================= OPINIE ================= */}
      <section className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionHead}>
          <span className={styles.kicker}>Opinie rodziców</span>
          <h2 className={styles.sectionTitle}>{process.env.REACT_APP_WHY_REVIEWS_HEADER || 'Zaufali nam rodzice z Olsztyna i okolic'}</h2>
        </div>
        <div className={styles.reviewsWrap}>
          <button className={styles.arrowBtn} onClick={scrollLeft} aria-label="Przewiń opinie w lewo"><IconArrow style={{ transform: 'rotate(180deg)' }} /></button>
          <div ref={scrollContainerRef} className={styles.reviewsScroll}>
            {reviews.map((r) => (
              <ReviewCard key={r.id} name={r.name} tag={r.tag} review={r.review} />
            ))}
          </div>
          <button className={styles.arrowBtn} onClick={scrollRight} aria-label="Przewiń opinie w prawo"><IconArrow /></button>
        </div>
      </section>

      {/* ================= WSPOLPRACA ================= */}
      <section className={`${styles.section} ${styles.coopSection}`}>
        <div className={styles.coopBox}>
          <img
            src="/image/program-za-zyciem.png"
            alt="Logo programu Za życiem"
            className={styles.coopLogo}
            width="424"
            height="119"
            loading="lazy"
          />
          <div className={styles.coopCopy}>
            <h2 className={styles.coopTitle}>Współpraca</h2>
            <p className={styles.coopText}>
              Współpracuję ze Specjalistycznym Ośrodkiem Szkolno-Wychowawczym w Olsztynie w ramach
              programu „Za życiem”.
            </p>
          </div>
        </div>
      </section>

      {/* ================= PORADNIK ================= */}
      <section className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionHead}>
          <span className={styles.kicker}>Poradnik</span>
          <h2 className={styles.sectionTitle}>Zanim umówisz diagnozę</h2>
          <p className={styles.sectionSub}>
            Odpowiedzi na pytania, które rodzice zadają mi najczęściej - o koszty, poradnie,
            dokumenty i przebieg badania.
          </p>
        </div>
        <div className={styles.serviceGrid}>
          {PORADNIK.map((p) => (
            <a key={p.to} href={p.to} className={styles.serviceCard}>
              <div className={styles.serviceBody}>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
                <span className={styles.serviceLink}>
                  Czytaj <IconArrow className={styles.linkArrow} />
                </span>
              </div>
            </a>
          ))}
        </div>
        <p style={{ textAlign: 'center', marginTop: '1.4rem' }}>
          <a href="/integracja-sensoryczna/" className={styles.btnOutline}>
            Zobacz cały poradnik o integracji sensorycznej{' '}
            <IconArrow className={styles.btnArrow} />
          </a>
        </p>
      </section>

      {/* ================= CTA BAND ================= */}
      <section className={styles.ctaBand}>
        <span className={`${styles.blob} ${styles.blobD}`} aria-hidden="true" />
        <div className={styles.ctaInner}>
          <h2>Zrób pierwszy krok dla rozwoju swojego dziecka</h2>
          <p>Umów diagnozę integracji sensorycznej w Olsztynie - wypełnij krótki kwestionariusz online lub zadzwoń.</p>
          <div className={styles.heroCtas}>
            <a href="/diagnoza-sensoryczna-olsztyn/" className={styles.btnPrimary} data-cta="booking" data-cta-location="cta-band">
              Umów diagnozę <IconArrow className={styles.btnArrow} />
            </a>
            <a href={`tel:${FIRMA.telefonE164}`} className={styles.btnGhost}>
              <IconPhone className={styles.btnIcon} /> {FIRMA.telefon}
            </a>
          </div>
        </div>
      </section>

      {/* ================= KONTAKT / NAP ================= */}
      <section className={`${styles.section} ${styles.contactSection}`}>
        <div className={styles.contactGrid}>
          <a className={styles.contactCard} href={MAPS_LINK} target="_blank" rel="noreferrer">
            <span className={styles.contactIcon}><IconPin /></span>
            <strong>{FIRMA.nazwa}</strong>
            <span>{FIRMA.ulica}<br />{FIRMA.kod} {FIRMA.miasto}</span>
          </a>
          <a className={styles.contactCard} href={`tel:${FIRMA.telefonE164}`}>
            <span className={styles.contactIcon}><IconPhone /></span>
            <strong>Telefon</strong>
            <span>{FIRMA.telefon}</span>
          </a>
          <a className={styles.contactCard} href={`mailto:${FIRMA.email}`}>
            <span className={styles.contactIcon}><IconMail /></span>
            <strong>E-mail</strong>
            <span>{FIRMA.email}</span>
          </a>
          <a className={styles.contactCard} href="/kontakt/">
            <span className={styles.contactIcon}><IconClock /></span>
            <strong>Godziny i dojazd</strong>
            <span>Pon.-Pt. {FIRMA.godziny[0].od}-{FIRMA.godziny[0].do}</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
